<template>
  <div class="markdown">
    <b-nav-item :to="{ path: `${makeLinkToAnchorCICD('gitlab')}` }" class="md__link-wrapper">
      <h2 class="md__item-title">Oversecured GitLab CI Integration</h2>
    </b-nav-item>

    <p>
      This guide will walk you through setting up a GitLab CI pipeline with Oversecured integration.
      The pipeline will automatically upload iOS or Android applications to the Oversecured platform
      for security scanning.
    </p>

    <p>
      <strong>Important:</strong> We provide ready-to-use GitLab CI templates, so you don't need to
      design anything from scratch. You can find these templates in our
      <a
        class="markdown__fit-link"
        href="https://gitlab.com/oversecuredinc/scan-submission"
        target="_blank"
        >public scan submission repository</a
      >:
    </p>
    <ul>
      <li>
        <a
          class="markdown__fit-link"
          href="https://gitlab.com/oversecuredinc/scan-submission/-/blob/main/.gitlab-ci.ios.yml"
          target="_blank"
          >iOS template (<code>.gitlab-ci.ios.yml</code>)</a
        >
      </li>
      <li>
        <a
          class="markdown__fit-link"
          href="https://gitlab.com/oversecuredinc/scan-submission/-/blob/main/.gitlab-ci.android.yml"
          target="_blank"
          >Android template (<code>.gitlab-ci.android.yml</code>)</a
        >
      </li>
    </ul>

    <h3 id="overview">Overview</h3>
    <p>
      Both iOS and Android pipelines use the same Docker image from the GitLab registry and submit
      scans the same way. The main difference is how the application artifact is provided:
    </p>
    <ul>
      <li><strong>iOS:</strong> The project must be compressed into a ZIP archive</li>
      <li><strong>Android:</strong> The pipeline uploads the built APK or AAB directly</li>
    </ul>

    <h3 id="ios-setup">iOS Pipeline Setup</h3>

    <h4 id="ios-repository">Step 1: Add GitLab CI Configuration</h4>
    <p>
      In your iOS repository, create a file named <code>.gitlab-ci.yml</code> and add the following
      configuration:
    </p>
    <pre
      class="language-yml"
    ><code class="language-yml"><span class="token key atrule">stages</span><span class="token punctuation">:</span>
  <span class="token punctuation">-</span> build
  <span class="token punctuation">-</span> scan

<span class="token key atrule">build-job</span><span class="token punctuation">:</span>
  <span class="token key atrule">stage</span><span class="token punctuation">:</span> build
  <span class="token key atrule">script</span><span class="token punctuation">:</span>
    <span class="token punctuation">-</span> apt<span class="token punctuation">-</span>get update <span class="token punctuation">&amp;&amp;</span> apt<span class="token punctuation">-</span>get install <span class="token punctuation">-</span>y zip
    <span class="token punctuation">-</span> zip <span class="token punctuation">-</span>r repo.zip .
  <span class="token key atrule">artifacts</span><span class="token punctuation">:</span>
    <span class="token key atrule">paths</span><span class="token punctuation">:</span>
      <span class="token punctuation">-</span> repo.zip
    <span class="token key atrule">expire_in</span><span class="token punctuation">:</span> <span class="token number">1</span> minute

<span class="token key atrule">oversecured-scanning-job</span><span class="token punctuation">:</span>
  <span class="token key atrule">stage</span><span class="token punctuation">:</span> scan
  <span class="token key atrule">allow_failure</span><span class="token punctuation">:</span> <span class="token boolean important">true</span>
  <span class="token key atrule">script</span><span class="token punctuation">:</span>
    <span class="token punctuation">-</span> echo <span class="token string">"Successfully sent for scanning to Oversecured"</span>
  <span class="token key atrule">image</span><span class="token punctuation">:</span> registry.gitlab.com/oversecuredinc/scan<span class="token punctuation">-</span>submission<span class="token punctuation">:</span>latest
  <span class="token key atrule">variables</span><span class="token punctuation">:</span>
    <span class="token key atrule">ZIP_FILE_NAME</span><span class="token punctuation">:</span> $CI_PROJECT_DIR/repo.zip
    <span class="token key atrule">API_KEY</span><span class="token punctuation">:</span> $OVERSECURED_API_KEY
    <span class="token key atrule">INTEGRATION_ID</span><span class="token punctuation">:</span> $OVERSECURED_INTEGRATION_ID
    <span class="token key atrule">INTEGRATION_BRANCH</span><span class="token punctuation">:</span> main
  <span class="token key atrule">dependencies</span><span class="token punctuation">:</span>
    <span class="token punctuation">-</span> build<span class="token punctuation">-</span>job</code></pre>

    <p><strong>Key points for iOS:</strong></p>
    <ul>
      <li>
        The build job compresses the entire repository into a ZIP file named <code>repo.zip</code>
      </li>
      <li>
        The ZIP file name must match exactly: the name used in the zip command, the name defined in
        artifacts, and the value of <code>ZIP_FILE_NAME</code>
      </li>
      <li>
        <code>CI_PROJECT_DIR</code> is mandatory as it provides an absolute path to the artifact
      </li>
    </ul>

    <h4 id="ios-platform">Step 2: Configure Platform Settings</h4>

    <h5>Create API Key</h5>
    <ol>
      <li>
        Go to
        <a
          class="markdown__fit-link"
          href="https://app.oversecured.com/settings/api-keys"
          target="_blank"
          >Oversecured Settings → API Keys</a
        >
      </li>
      <li>Generate a new API key</li>
      <li>Copy the value</li>
    </ol>

    <h5>Create iOS Integration</h5>
    <ol>
      <li>Go to <strong>Apps</strong></li>
      <li>Click <strong>Add new app</strong></li>
      <li>Choose <strong>iOS</strong> platform</li>
      <li>Name it however you want</li>
      <li>Submit the request</li>
      <li>After creation, copy the integration ID from the UI or URL</li>
    </ol>

    <h5>Add GitLab CI Variables</h5>
    <ol>
      <li>Open your GitLab project settings</li>
      <li>Go to <strong>CI/CD → Variables</strong></li>
      <li>Add a variable named <code>OVERSECURED_API_KEY</code> with your API key value</li>
      <li>Add a variable named <code>OVERSECURED_INTEGRATION_ID</code> with your integration ID</li>
      <li>Set both variables to <strong>masked</strong> and <strong>hidden</strong></li>
    </ol>

    <p>
      <strong>Important note about protected variables:</strong> If a variable is marked as
      protected, it is only available on protected branches or tags. If your pipeline runs on an
      unprotected branch, the variable will not be injected and the job will fail.
    </p>

    <h4 id="ios-run">Step 3: Commit and Run</h4>
    <p>
      After committing and pushing the <code>.gitlab-ci.yml</code> file, GitLab will automatically
      start the pipeline:
    </p>
    <ul>
      <li>The build job compresses the project</li>
      <li>The artifact is uploaded</li>
      <li>The scan job runs and uploads the archive to Oversecured</li>
      <li>A new scan version is created</li>
    </ul>
    <p>
      You can verify the scan by going to the Oversecured platform and opening your app. You should
      see a new scan in progress.
    </p>

    <h3 id="android-setup">Android Pipeline Setup</h3>

    <h4 id="android-repository">Step 1: Add GitLab CI Configuration</h4>
    <p>
      For Android, you typically already have a build job that produces an APK or AAB file. Add the
      scan job to your existing pipeline:
    </p>
    <pre
      class="language-yml"
    ><code class="language-yml"><span class="token key atrule">stages</span><span class="token punctuation">:</span>
  <span class="token punctuation">-</span> build
  <span class="token punctuation">-</span> scan

<span class="token key atrule">build-job</span><span class="token punctuation">:</span>
  <span class="token key atrule">stage</span><span class="token punctuation">:</span> build
  <span class="token key atrule">image</span><span class="token punctuation">:</span> fabernovel/android<span class="token punctuation">:</span>api<span class="token punctuation">-</span><span class="token number">35</span><span class="token punctuation">-</span>v1.10.1
  <span class="token key atrule">before_script</span><span class="token punctuation">:</span>
    <span class="token punctuation">-</span> export GRADLE_USER_HOME=$CI_PROJECT_DIR/.gradle
    <span class="token punctuation">-</span> chmod +x ./gradlew
  <span class="token key atrule">variables</span><span class="token punctuation">:</span>
    <span class="token key atrule">GRADLE_OPTS</span><span class="token punctuation">:</span> <span class="token string">"-Dorg.gradle.daemon=false"</span>
  <span class="token key atrule">script</span><span class="token punctuation">:</span>
    <span class="token punctuation">-</span> ./gradlew assembleDebug
  <span class="token key atrule">artifacts</span><span class="token punctuation">:</span>
    <span class="token key atrule">paths</span><span class="token punctuation">:</span>
      <span class="token punctuation">-</span> app/build/outputs/apk/debug/app<span class="token punctuation">-</span>debug.apk
    <span class="token key atrule">expire_in</span><span class="token punctuation">:</span> <span class="token number">1</span> minute
  <span class="token key atrule">cache</span><span class="token punctuation">:</span>
    <span class="token key atrule">key</span><span class="token punctuation">:</span> ${CI_COMMIT_REF_SLUG}
    <span class="token key atrule">paths</span><span class="token punctuation">:</span>
      <span class="token punctuation">-</span> .gradle/wrapper
      <span class="token punctuation">-</span> .gradle/caches

<span class="token key atrule">oversecured-scanning-job</span><span class="token punctuation">:</span>
  <span class="token key atrule">stage</span><span class="token punctuation">:</span> scan
  <span class="token key atrule">allow_failure</span><span class="token punctuation">:</span> <span class="token boolean important">true</span>
  <span class="token key atrule">script</span><span class="token punctuation">:</span>
    <span class="token punctuation">-</span> echo <span class="token string">"Successfully sent for scanning to Oversecured"</span>
  <span class="token key atrule">image</span><span class="token punctuation">:</span> registry.gitlab.com/oversecuredinc/scan<span class="token punctuation">-</span>submission<span class="token punctuation">:</span>latest
  <span class="token key atrule">variables</span><span class="token punctuation">:</span>
    <span class="token key atrule">APP_PATH</span><span class="token punctuation">:</span> $CI_PROJECT_DIR/app/build/outputs/apk/debug/app<span class="token punctuation">-</span>debug.apk
    <span class="token key atrule">API_KEY</span><span class="token punctuation">:</span> $OVERSECURED_API_KEY
    <span class="token key atrule">INTEGRATION_ID</span><span class="token punctuation">:</span> $OVERSECURED_INTEGRATION_ID
    <span class="token key atrule">INTEGRATION_BRANCH</span><span class="token punctuation">:</span> main
  <span class="token key atrule">dependencies</span><span class="token punctuation">:</span>
    <span class="token punctuation">-</span> build<span class="token punctuation">-</span>job</code></pre>

    <p><strong>Key points for Android:</strong></p>
    <ul>
      <li>No compression is needed - the pipeline uploads the built APK or AAB directly</li>
      <li>Instead of <code>ZIP_FILE_NAME</code>, you define <code>APP_PATH</code></li>
      <li>
        <code>APP_PATH</code> should point to the absolute path of your APK/AAB file, typically
        using <code>$CI_PROJECT_DIR</code>
      </li>
      <li>Example path: <code>$CI_PROJECT_DIR/app/build/outputs/apk/debug/app-debug.apk</code></li>
    </ul>

    <h4 id="android-platform">Step 2: Configure Platform Settings</h4>
    <p>Repeat the same steps as for iOS:</p>
    <ol>
      <li>Create a new app in Oversecured platform</li>
      <li>Choose <strong>Android</strong> platform</li>
      <li>Copy the integration ID</li>
      <li>
        Add GitLab CI variables:
        <ul>
          <li><code>OVERSECURED_API_KEY</code> (same as iOS, or create a new one)</li>
          <li><code>OVERSECURED_INTEGRATION_ID</code> (your Android integration ID)</li>
        </ul>
      </li>
      <li>Set both variables to <strong>masked</strong> and <strong>hidden</strong></li>
    </ol>

    <h4 id="android-run">Step 3: Commit and Run</h4>
    <p>After committing the changes:</p>
    <ul>
      <li>The Android build job runs and produces the APK/AAB</li>
      <li>The scan job uploads the file to Oversecured</li>
      <li>A new scan is created on the platform</li>
    </ul>
    <p>
      You can verify in both GitLab logs (which show the recognized platform) and the Oversecured UI
      (which shows the scan in progress).
    </p>

    <h3 id="summary">Summary</h3>
    <p>To summarize the key differences and requirements:</p>
    <ul>
      <li><strong>iOS:</strong> Requires ZIP archive compression before upload</li>
      <li><strong>Android:</strong> Uploads build output (APK/AAB) directly</li>
      <li>
        <strong>Variables:</strong> Must match pipeline names exactly
        (<code>OVERSECURED_API_KEY</code>, <code>OVERSECURED_INTEGRATION_ID</code>)
      </li>
      <li><strong>Protected variables:</strong> Only available on protected branches/tags</li>
      <li>
        <strong>Templates:</strong> Ready-to-use templates are available in our
        <a
          class="markdown__fit-link"
          href="https://gitlab.com/oversecuredinc/scan-submission"
          target="_blank"
          >scan submission repository</a
        >
      </li>
    </ul>

    <p>GitLab CI templates source code:</p>
    <ul>
      <li>
        <a
          class="markdown__fit-link"
          href="https://gitlab.com/oversecuredinc/scan-submission/-/blob/main/.gitlab-ci.ios.yml"
          target="_blank"
          >iOS template (<code>.gitlab-ci.ios.yml</code>)</a
        >
      </li>
      <li>
        <a
          class="markdown__fit-link"
          href="https://gitlab.com/oversecuredinc/scan-submission/-/blob/main/.gitlab-ci.android.yml"
          target="_blank"
          >Android template (<code>.gitlab-ci.android.yml</code>)</a
        >
      </li>
      <li>
        <a
          class="markdown__fit-link"
          href="https://gitlab.com/oversecuredinc/scan-submission"
          target="_blank"
          >Repository</a
        >
      </li>
    </ul>
  </div>
</template>

<script>
import { makeAnchor, makeLinkToAnchor, makeLinkToAnchorCICD } from '../../../services/functions';
import { makeSubLinkToAnchor } from '@/services/functions';

export default {
  methods: {
    makeAnchor,
    makeLinkToAnchor,
    makeSubLinkToAnchor,
    makeLinkToAnchorCICD,
  },
};
</script>

<style scoped></style>
